import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureToggle } from '@/common/api/datasources/Api';
import { FeatureToggleItem } from './FeatureToggleItem';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, AlertTriangle } from 'lucide-react';

interface SortableFeatureTogglesProps {
    toggles: FeatureToggle[];
    onToggle: (toggle: FeatureToggle) => void;
    onReorder: (toggle: FeatureToggle, newOrder: number) => void;
    isUpdating: boolean | ((key: string) => boolean);
    isReordering: boolean | ((key: string) => boolean);
}

// Компонент для сортування окремого елемента
const SortableToggleItem = ({
    toggle,
    onToggle,
    isUpdating,
    isReordering
}: {
    toggle: FeatureToggle;
    onToggle: (toggle: FeatureToggle) => void;
    isUpdating: boolean | ((key: string) => boolean);
    isReordering: boolean | ((key: string) => boolean);
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: toggle.key,
        disabled: (typeof isUpdating === 'function' && isUpdating(toggle.key)) ||
            (typeof isReordering === 'function' && isReordering(toggle.key))
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : undefined,
        opacity: isDragging ? 0.5 : 1, // Додаємо прозорість для драг-елемента
    };

    // Видаляємо listeners з загальних атрибутів, щоб передати їх тільки для dragHandle
    const { ...restAttributes } = attributes;
    const dragHandleProps = { ...listeners };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...restAttributes}
        >
            <FeatureToggleItem
                toggle={toggle}
                onToggle={onToggle}
                isUpdating={isUpdating}
                isReordering={isReordering}
                isDraggable={true}
                className={isDragging ? "shadow-lg ring-2 ring-primary" : ""}
                dragHandleProps={dragHandleProps}
            />
        </div>
    );
};

export const SortableFeatureToggles = ({
    toggles,
    onToggle,
    onReorder,
    isUpdating,
    isReordering,
}: SortableFeatureTogglesProps) => {
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);
    const [activeId, setActiveId] = useState<string | null>(null);

    // Локальний стан для оптимістичного оновлення UI
    const [localToggles, setLocalToggles] = useState<FeatureToggle[]>(toggles);

    // Оновлюємо локальний стан при оновленні даних з пропсів
    useEffect(() => {
        setLocalToggles([...toggles].sort((a, b) => a.order - b.order));
    }, [toggles]);

    // Знаходження активного елемента під час перетягування
    const activeToggle = activeId ? localToggles.find(toggle => toggle.key === activeId) : null;

    // Налаштування датчиків для drag-and-drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // Має бути рух мінімум на 5px перед початком перетягування
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Обробник початку перетягування
    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    // Обробник завершення перетягування
    const handleDragEnd = (event: DragEndEvent) => {
        setActiveId(null);
        const { active, over } = event;

        // Якщо немає цільового елемента, або це той самий елемент - виходимо
        if (!over || active.id === over.id) {
            return;
        }

        try {
            // Знаходимо індекси елементів для локального переміщення
            const activeIndex = localToggles.findIndex(toggle => toggle.key === active.id);
            const overIndex = localToggles.findIndex(toggle => toggle.key === over.id);

            if (activeIndex === -1 || overIndex === -1) {
                return;
            }

            // Оптимістично оновлюємо UI - переміщуємо елемент локально
            const newToggles = arrayMove([...localToggles], activeIndex, overIndex);
            setLocalToggles(newToggles);

            // Отримуємо тогл, який переміщуємо
            const movedToggle = localToggles[activeIndex];

            // Визначаємо новий порядок для тогла
            let newOrder: number;

            if (overIndex === 0) {
                // Переміщення на початок списку
                newOrder = localToggles[0].order - 10;
            } else if (overIndex === localToggles.length - 1) {
                // Переміщення в кінець списку
                newOrder = localToggles[localToggles.length - 1].order + 10;
            } else {
                // Переміщення в середину списку
                // Вибираємо сусідні елементи після переміщення
                const prevToggle = overIndex > 0 ? newToggles[overIndex - 1] : newToggles[0];
                const nextToggle = overIndex < newToggles.length - 1 ? newToggles[overIndex + 1] : newToggles[overIndex - 1];
                newOrder = Math.floor((prevToggle.order + nextToggle.order) / 2);
            }

            // Викликаємо функцію для оновлення порядку на сервері
            onReorder(movedToggle, newOrder);
        } catch (err) {
            setError(t('systemSettings.featureToggles.errors.reorder'));
            console.error('Error reordering toggles:', err);
        }
    };

    if (localToggles.length === 0) {
        return (
            <Alert variant="default" className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t('systemSettings.featureToggles.noToggles.title')}</AlertTitle>
                <AlertDescription>
                    {t('systemSettings.featureToggles.noToggles.description')}
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-4">
            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{t('common.errors.occurred')}</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]} // Обмежуємо перетягування тільки по вертикалі
            >
                <SortableContext
                    items={localToggles.map(toggle => toggle.key)}
                    strategy={verticalListSortingStrategy}
                >
                    <div className="space-y-3">
                        {localToggles.map((toggle) => (
                            <SortableToggleItem
                                key={toggle.key}
                                toggle={toggle}
                                onToggle={onToggle}
                                isUpdating={isUpdating}
                                isReordering={isReordering}
                            />
                        ))}
                    </div>
                </SortableContext>

                {/* Оверлей для перетягування елемента */}
                <DragOverlay adjustScale={false}>
                    {activeToggle ? (
                        <FeatureToggleItem
                            toggle={activeToggle}
                            onToggle={onToggle}
                            isUpdating={isUpdating}
                            isReordering={isReordering}
                            isDraggable={true}
                            className="shadow-lg ring-2 ring-primary bg-background"
                        />
                    ) : null}
                </DragOverlay>
            </DndContext>
        </div>
    );
}; 